import React, { useEffect } from 'react';
import styled from 'styled-components';
import { spacer16, Type } from '@spotify-internal/encore-web';

import Metatags from 'components/Metatags/Metatags';
import PageContainer from 'components/PageContainer';
import Breadcrumbs from 'components/Breadcrumbs';
import BlockSerializer from 'lib/sanity/ui/BlockSerializer';
import WarningBanner from 'components/WarningBanner/WarningBanner';
import analytics from 'utils/client/analytics/analytics';
import { useUbiArticlePage } from 'utils/ubi/pages/useUbiArticle';
import { BlockSerializerUbiContext } from 'lib/sanity/ui/BlockSerializerUbiProvider';
import { TreatedArticleData, TreatmentState } from 'utils/remote-config/content/types';
import FeedbackWidgetControl from 'experiments/feedback-position';
import { AlternateLink } from 'data/market/types';
import { useServerSideConstants } from 'providers/ServerSideConstantsProvider';

import RelatedArticles from './RelatedArticles';
import StyledCard from './StyledCard';
import CallToActionCard from './CallToActionCard';

export interface ArticleProps {
  article: TreatedArticleData;
  articleSlug: string;
  alternateLinks: AlternateLink[];
}

const Title = styled(Type)`
  word-break: break-word;
  hyphens: auto;
`;

// CTAs might be on top/bottom of the article content
// Setting the outer container use `flex`
// so that child elements can set `order` value for placement
const OrderedSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

function getTreatmentIdentifier(article: TreatedArticleData, articleSlug: string): string {
  switch (article.treatmentState) {
    case TreatmentState.Control:
      return `${articleSlug}_control`;
    case TreatmentState.Treatment:
      return `${articleSlug}_treatment_${article.treatmentKey}`;
    default:
      return articleSlug;
  }
}

const Article = ({ article, articleSlug, alternateLinks = [] }: ArticleProps) => {
  const articleIdentifier = getTreatmentIdentifier(article, articleSlug);
  const ubi = useUbiArticlePage(articleIdentifier);
  const { isPlain } = useServerSideConstants();

  useEffect(() => {
    if (articleSlug !== articleIdentifier) {
      analytics.trackExperiment(articleIdentifier);
    }
    ubi.logArticleImpression();
  }, [articleIdentifier, ubi, articleSlug]);

  const { content, title, relatedArticles, category, banner, callToActionCard } = article;

  const parentCategory = category?.parentCategory;

  // Take the `callToActionCard` directly referenced by the article first
  // Then look up on its `parentCategory`
  const prioCallToActionCard = callToActionCard || category?.parentCategory?.callToActionCard;

  return (
    <>
      <Metatags title={title as string} urlPath={`article/${articleSlug}`} alternateLinks={alternateLinks} />

      {!isPlain && <WarningBanner banner={banner} />}

      <PageContainer>
        {!isPlain && parentCategory && <Breadcrumbs parentCategory={parentCategory} article={article} ubi={ubi} />}

        <OrderedSectionContainer>
          {/* Do not show if `title` is not localized  */}
          {!isPlain && prioCallToActionCard?.title && <CallToActionCard {...prioCallToActionCard} ubi={ubi} />}

          {content && (
            <StyledCard>
              <Title forwardedAs="h1" variant="altoBrio" paddingBottom={spacer16}>
                {title}
              </Title>
              <BlockSerializerUbiContext.Provider value={ubi}>
                <BlockSerializer value={content} />
              </BlockSerializerUbiContext.Provider>
            </StyledCard>
          )}

          {!isPlain && relatedArticles.length > 0 && (
            <StyledCard>
              <RelatedArticles relatedArticles={relatedArticles} ubi={ubi} />
            </StyledCard>
          )}
        </OrderedSectionContainer>

        {!isPlain && <FeedbackWidgetControl articleIdentifier={articleIdentifier} ubi={ubi} />}
      </PageContainer>
    </>
  );
};

export default Article;
